import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { esMatricula, formatearMatricula } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

const i = String.fromCharCode(0xed);

export function etiquetaMatricula(m) {
  const mat = (m?.matricula || "").trim();
  const alias = (m?.alias || "").trim();
  return alias ? `${mat} \u00b7 ${alias}` : mat;
}

export default function MatriculaSelect({
  transportistaNif,
  transportistaRazon,
  transportistaCodigo,
  tipo,
  value,
  onChange,
  testid,
  label,
  required,
}) {
  const fid = React.useId();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [alta, setAlta] = useState(false);
  const [draft, setDraft] = useState("");
  const [alias, setAlias] = useState("");
  const [busy, setBusy] = useState(false);
  const nifRaw = (transportistaNif || "").trim().toUpperCase();
  const nif = nifRaw.startsWith("COD-") ? "" : nifRaw;
  const codigo = transportistaCodigo != null && transportistaCodigo !== "" ? transportistaCodigo : (nifRaw.startsWith("COD-") ? nifRaw.slice(4) : "");
  const esRemolque = tipo === "remolque";
  const titulo = label || (esRemolque ? "Remolque" : "Cami" + String.fromCharCode(0xf3) + "n / furgoneta");

  const paramsMats = () => ({
    transportista_nif: nif || undefined,
    transportista_codigo: codigo || undefined,
    tipo: tipo || "vehiculo",
  });

  const reload = async () => {
    if (!nif && !codigo) {
      setItems([]);
      return;
    }
    const { data } = await api.get("/api/v1/matriculas", { params: paramsMats() });
    setItems((data.items || []).filter((c) => c.activo !== false));
  };

  useEffect(() => {
    if (!nif && !codigo) {
      setItems([]);
      setAlta(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/v1/matriculas", { params: paramsMats() });
        if (!cancelled) setItems((data.items || []).filter((c) => c.activo !== false));
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [nif, codigo, tipo, user?.sage_conductores]); // eslint-disable-line

  const guardarAlta = async () => {
    const mat = formatearMatricula(draft);
    if (!esMatricula(draft) || !mat) {
      toast.error("Matr" + i + "cula no v" + String.fromCharCode(0xe1) + "lida (espa" + String.fromCharCode(0xf1) + "ola o extranjera: letras y n" + String.fromCharCode(0xfa) + "meros)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/v1/matriculas", {
        transportista_nif: nif || (codigo ? "COD-" + codigo : ""),
        transportista_razon_social: transportistaRazon || "",
        tipo: tipo || "vehiculo",
        matricula: mat,
        alias: alias.trim() || null,
        activo: true,
      });
      await reload();
      onChange(mat);
      setDraft("");
      setAlias("");
      setAlta(false);
      toast.success("Matr" + i + "cula a" + String.fromCharCode(0xf1) + "adida al maestro");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!nif && !codigo) {
    return (
      <div>
        <Label htmlFor={fid} className="label-tag">{titulo}{required ? " *" : ""}</Label>
        <div id={fid} className="mt-1 text-xs text-slate-500 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
          Elige primero el transportista.
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={fid} className="label-tag">{titulo}{required ? " *" : ""}</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger id={fid} className="mt-1" data-testid={testid}>
          <SelectValue placeholder={"Selecciona matr" + i + "cula"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{esRemolque ? "Sin remolque" : "Sin matr" + i + "cula"}</SelectItem>
          {items.map((c) => (
            <SelectItem key={c.id} value={c.matricula}>{etiquetaMatricula(c)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2">
        {items.length === 0 ? (
          <span>
            {esRemolque
              ? "Este transportista no tiene remolques. "
              : "Este transportista no tiene veh" + i + "culos. "}
          </span>
        ) : null}
        <button type="button" className="text-blue-700 underline" onClick={() => setAlta((v) => !v)}>
          {alta ? "Cerrar alta" : "Alta r" + String.fromCharCode(0xe1) + "pida"}
        </button>
        <Link to="/matriculas" className="text-blue-700 underline">Mantenimiento</Link>
      </div>
      {alta ? (
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
          <Input
            className="font-mono uppercase"
            value={draft}
            maxLength={LEN.matricula}
            onChange={(ev) => setDraft(ev.target.value)}
            placeholder={esRemolque ? "R-1234-BCD" : "1234-BCD"}
            aria-label={titulo}
            data-testid={testid ? `${testid}-alta` : undefined}
          />
          <Button type="button" size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={guardarAlta}>
            Guardar
          </Button>
          <Input
            className="col-span-2"
            value={alias}
            maxLength={40}
            onChange={(ev) => setAlias(ev.target.value)}
            placeholder="Alias (opcional)"
            aria-label="Alias (opcional)"
          />
        </div>
      ) : null}
    </div>
  );
}
